import { isAfterLocalToday } from "../../../utils";
import { NumericInput } from "../types";

// Mirrors the API's `priceWriter.ts` limits. These are a fast, local response;
// the server remains authoritative because the value is persisted there.
export const MAX_PRICE_VALUE = 999_999_999_999;
export const MAX_FX_RATE_VALUE = 9_999_999_999;
const PRICE_DECIMALS = 8;

// Counts the decimals of the *number*, not of its shortest string form. JS stringifies anything
// below 1e-6 in exponential notation, so reading the text alone would report a fabricated digit
// count for 0.0000001 — a price with seven decimals, which this limit allows.
const fractionalDigits = (value: number): number => {
  const [mantissa, exponent] = String(value).toLowerCase().split("e");
  const decimalIndex = mantissa.indexOf(".");
  const decimals = decimalIndex === -1 ? 0 : mantissa.length - decimalIndex - 1;
  if (exponent === undefined) {
    return decimals;
  }
  return Math.max(0, decimals - Number(exponent));
};

export const validateOverrideDate = (value: Date | null): string | true => {
  if (!value || Number.isNaN(value.getTime())) {
    return "Date is required and must be valid";
  }
  if (isAfterLocalToday(value)) {
    return "Date cannot be in the future";
  }
  return true;
};

const validatePositiveDecimal = (
  value: NumericInput,
  label: "Price" | "FX rate",
  maximum: number,
  required: boolean,
): string | true => {
  if (value === "" || value === undefined || value === null) {
    return required ? `${label} is required` : true;
  }
  if (!Number.isFinite(value) || value <= 0) {
    return `${label} must be greater than 0`;
  }
  // Magnitude before precision: a number past the ceiling is rejected whatever its decimals are, so
  // reporting the ceiling is the message that tells the user what to change.
  if (value > maximum) {
    return `${label} is too large`;
  }
  if (fractionalDigits(value) > PRICE_DECIMALS) {
    return `${label} must have at most ${PRICE_DECIMALS} decimal places`;
  }
  return true;
};

export const validateOverridePrice = (value: NumericInput) =>
  validatePositiveDecimal(value, "Price", MAX_PRICE_VALUE, true);

export const validateOverrideFxRate = (value: NumericInput) =>
  validatePositiveDecimal(value, "FX rate", MAX_FX_RATE_VALUE, false);
